import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Loader2, Activity, Users, Clock } from "lucide-react";
import { useAuth, homeFor } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import api from "@/lib/api";

const DEMO = [
  { role: "Admin", email: "admin@gosource.local" },
  { role: "Manager", email: "manager@gosource.local" },
  { role: "Employee", email: "employee1@gosource.local" },
];

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [forgotMsg, setForgotMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password, remember);
      nav(homeFor(u.role));
    } catch (err) {
      setError(apiErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (d) => {
    setEmail(d.email);
    setPassword("GoSource@123");
    setError("");
  };

  const sendForgot = async () => {
    setForgotMsg("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setForgotMsg(data.message);
    } catch (err) {
      setForgotMsg(apiErr(err.response?.data?.detail));
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-[#0B1F33] p-12 text-white lg:flex">
        <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[#08A7A0]/20 blur-3xl" />
        <div className="absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-[#08A7A0]/10 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#08A7A0] font-display text-2xl font-extrabold">G</div>
          <div>
            <p className="font-display text-lg font-extrabold">GoSource</p>
            <p className="text-xs font-semibold uppercase tracking-widest text-teal-300">Workforce Control</p>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-display text-4xl font-extrabold leading-tight">
            Command your <span className="text-[#08A7A0]">RCM workforce</span> in real time.
          </h1>
          <p className="mt-4 max-w-md text-slate-300">
            Break tracking, live attendance, exception alerts and executive reporting — purpose-built for medical billing operations.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4">
            {[{ i: Activity, t: "Live Monitor" }, { i: Clock, t: "Break Policy" }, { i: Users, t: "Team Insights" }].map((f, idx) => (
              <div key={idx} className="rounded-xl border border-white/10 bg-white/5 p-4">
                <f.i className="h-6 w-6 text-[#08A7A0]" />
                <p className="mt-2 text-sm font-semibold">{f.t}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="relative text-xs text-slate-400">Demo environment · Sample data marked as demo · © GoSource</p>
      </div>

      {/* Right form */}
      <div className="flex w-full items-center justify-center bg-[#F4F7FA] px-6 py-12 lg:w-1/2">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#08A7A0] font-display text-lg font-extrabold text-white">G</div>
              <p className="font-display text-lg font-extrabold text-[#0B1F33]">GoSource Workforce Control</p>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-lg">
            <h2 className="font-display text-2xl font-bold text-[#0B1F33]">Sign in</h2>
            <p className="mt-1 text-sm text-slate-500">Access your workforce dashboard</p>

            {!forgot ? (
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Employee ID / Email</label>
                  <input
                    data-testid="login-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1.5 w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm outline-none transition focus:border-[#08A7A0] focus:ring-2 focus:ring-[#08A7A0]/20"
                    placeholder="you@gosource.local"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Password</label>
                  <input
                    data-testid="login-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1.5 w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm outline-none transition focus:border-[#08A7A0] focus:ring-2 focus:ring-[#08A7A0]/20"
                    placeholder="••••••••"
                    required
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    <input type="checkbox" data-testid="login-remember" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-[#08A7A0]" />
                    Remember me
                  </label>
                  <button type="button" onClick={() => setForgot(true)} className="text-sm font-semibold text-[#08A7A0] hover:underline">
                    Forgot password?
                  </button>
                </div>

                {error && <div data-testid="login-error" className="rounded-lg bg-red-50 px-3 py-2.5 text-sm font-medium text-red-700">{error}</div>}

                <button
                  data-testid="login-submit"
                  disabled={loading}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#08A7A0] py-2.5 text-sm font-bold text-white transition hover:bg-[#06827C] disabled:opacity-60"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                  Sign in
                </button>
              </form>
            ) : (
              <div className="mt-6 space-y-4">
                <p className="text-sm text-slate-600">Enter your email and we'll generate a reset link (shown in server logs for this demo).</p>
                <input
                  data-testid="forgot-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm outline-none focus:border-[#08A7A0]"
                  placeholder="you@gosource.local"
                />
                {forgotMsg && <div className="rounded-lg bg-teal-50 px-3 py-2.5 text-sm text-teal-700">{forgotMsg}</div>}
                <div className="flex gap-2">
                  <button onClick={sendForgot} data-testid="forgot-submit" className="flex-1 rounded-lg bg-[#08A7A0] py-2.5 text-sm font-bold text-white hover:bg-[#06827C]">Send reset link</button>
                  <button onClick={() => setForgot(false)} className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-600">Back</button>
                </div>
              </div>
            )}
          </div>

          {/* Demo quick logins */}
          <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Demo accounts · password <span className="font-mono text-[#08A7A0]">GoSource@123</span></p>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {DEMO.map((d) => (
                <button
                  key={d.role}
                  data-testid={`demo-${d.role.toLowerCase()}`}
                  onClick={() => fillDemo(d)}
                  className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-2.5 text-xs font-bold text-[#0B1F33] transition hover:border-[#08A7A0] hover:bg-teal-50"
                >
                  {d.role}
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
